import React, { useCallback, useEffect, useMemo } from 'react';
import { AudioElement, ElementUpdateTypes } from '@bynder-studio/render-core';
import { PlaybackManipulationRenderer } from '@bynder-studio/render-web';
import { useTranslate } from '@bynder/localization';
import { IconHide, IconShow, IconVolumeOff, IconVolumeUp } from '@bynder/icons';
import useEditor from '../../../../../hooks/useEditor';
import useForceUpdate from '../../../../../hooks/useForceUpdate';
import { collectTopLevelElements } from './utils';
import generateTestId from 'packages/helpers/testIdHelpers';
import { TimelineActionsIcon } from '../TimelineActions.styled';
import { parseShortcutForTooltip } from 'packages/common/WithShortcutLabel';

const testId = generateTestId('timeline_actions_visibility');

export default function VisibilityAction() {
    const { translate } = useTranslate();
    const forceUpdate = useForceUpdate();
    const { creativeModel, manipulationRenderer } = useEditor();
    const selectedElements = manipulationRenderer?.getSelectedElements() || [];

    const isActive = !!selectedElements.length;
    const isSelected = isActive && collectTopLevelElements(selectedElements).every((el) => el.hidden);

    const showAudioIcons = useMemo(
        () => selectedElements.every((el) => el instanceof AudioElement),
        [selectedElements],
    );

    const handleVisibilityChange = useCallback(() => {
        if (!isActive) {
            return;
        }

        if (manipulationRenderer instanceof PlaybackManipulationRenderer) {
            manipulationRenderer.pausePlayback();
        }

        collectTopLevelElements(selectedElements).forEach((element) => {
            creativeModel.updateElement(element.id, { hidden: !isSelected });
        });

        forceUpdate();
    }, [isActive, isSelected, selectedElements, creativeModel, forceUpdate, manipulationRenderer]);

    const handleVisibilityChangeAction = useCallback(
        ({ updateTypes, element }) => {
            if (updateTypes.has(ElementUpdateTypes.VISIBILITY) && selectedElements.some((el) => el.id === element.id)) {
                forceUpdate();
            }
        },
        [selectedElements, forceUpdate],
    );

    useEffect(() => {
        if (!manipulationRenderer) {
            return;
        }

        manipulationRenderer.eventEmitter.on('elementSelected', forceUpdate);

        return () => {
            manipulationRenderer.eventEmitter.off('elementSelected', forceUpdate);
        };
    }, [forceUpdate, manipulationRenderer]);

    useEffect(() => {
        if (!creativeModel) {
            return;
        }

        return creativeModel.onElementsPartialUpdate(ElementUpdateTypes.VISIBILITY, handleVisibilityChangeAction);
    }, [creativeModel, handleVisibilityChangeAction]);

    return (
        <div title={`${translate('editor.layers.sidebar.show-hide')} (${parseShortcutForTooltip(['cmd', 'h'])})`}>
            <TimelineActionsIcon onClick={handleVisibilityChange} active={isActive} selected={isSelected} {...testId}>
                {showAudioIcons ? (
                    isSelected ? (
                        <IconVolumeOff />
                    ) : (
                        <IconVolumeUp />
                    )
                ) : isSelected ? (
                    <IconHide />
                ) : (
                    <IconShow />
                )}
            </TimelineActionsIcon>
        </div>
    );
}
